import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import { collection, getDocs, query } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import AccordionImageButton from './AccordionImageButton';
import './LeftAccordion.css';

// In-memory cache for hierarchy (survives component unmounts)
let cachedHierarchy = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function LeftAccordion({ selectedPath, onPathChange, onHierarchyLoad, user, onClose, onDoneSearching, isCollapsed, onAlbumSelect, isFocused, showNavLinks, setShowNavLinks, selectedArtists = [], onArtistToggle, isMasterPlaylistMode = false, onMasterPlaylistModeChange, selectedFromMasterList = [], onMasterListArtistToggle }) {
  const navigate = useNavigate();
  
  // Log mount only once (not on every render)
  useEffect(() => {
    console.log('🔵 LeftAccordion MOUNTED - Version 2.0 - NO CACHING CODE');
  }, []); // Empty deps = only on mount
  const [expanded, setExpanded] = useState({
    albums: true, // Show Albums by default
    singles: true, // Show Singles by default
    languages: {},
    genres: {},
    subgenres: {},
    artists: {}
  });
  
  // Gamepad navigation - DOM-based approach for simplicity
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const accordionRef = React.useRef(null);
  const artistsScrollRef = React.useRef(null);
  
  // Get all clickable buttons in the accordion
  const getNavButtons = () => {
    if (!accordionRef.current) return [];
    return Array.from(accordionRef.current.querySelectorAll('.accordion-image-button'));
  };
  
  // Track which button text was focused before structure change
  const lastFocusedText = React.useRef(null);
  
  // Handle wheel scrolling for artists container - snap to rows
  useEffect(() => {
    const artistsContainer = artistsScrollRef.current;
    if (!artistsContainer) return;

    const handleWheel = (e) => {
      // Only handle if artists row is expanded
      if (!selectedPath.language) return;

      // Calculate row height: button height + gap
      const firstButton = artistsContainer.querySelector('.nav-button');
      if (!firstButton) return;
      
      const buttonHeight = firstButton.offsetHeight;
      const buttonsContainer = artistsContainer.querySelector('.nav-row-buttons');
      const computedStyle = window.getComputedStyle(buttonsContainer);
      const gap = parseFloat(computedStyle.gap) || 8; // Default to 8px if gap not found
      const rowHeight = buttonHeight + gap;

      // Prevent default scrolling
      e.preventDefault();

      // Determine scroll direction
      const scrollDelta = e.deltaY > 0 ? rowHeight : -rowHeight;
      
      // Scroll by one row
      artistsContainer.scrollBy({
        top: scrollDelta,
        behavior: 'smooth'
      });
    };

    artistsContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      artistsContainer.removeEventListener('wheel', handleWheel);
    };
  }, [selectedPath.type]);
  
  // Listen for gamepad navigation events
  useEffect(() => {
    const handleNavigate = (e) => {
      // Only handle navigation when this panel is focused
      if (!isFocused) return;
      
      const { direction } = e.detail;
      const buttons = getNavButtons();
      
      if (buttons.length === 0) return;
      
      // Remove focus from all
      buttons.forEach(btn => btn.classList.remove('gamepad-focused'));
      
      let newIndex = focusedIndex;
      
      if (direction === 'up') {
        newIndex = focusedIndex > 0 ? focusedIndex - 1 : buttons.length - 1;
      } else if (direction === 'down') {
        newIndex = focusedIndex < buttons.length - 1 ? focusedIndex + 1 : 0;
      } else if (direction === 'right' || direction === 'left') {
        // Trigger click on current item (expand/collapse)
        if (focusedIndex >= 0 && buttons[focusedIndex]) {
          // Save current button text so we can find it after DOM changes
          lastFocusedText.current = buttons[focusedIndex].textContent.trim();
          buttons[focusedIndex].click();
          return;
        }
      }
      
      // Apply focus to new item
      if (buttons[newIndex]) {
        buttons[newIndex].classList.add('gamepad-focused');
        buttons[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setFocusedIndex(newIndex);
        lastFocusedText.current = buttons[newIndex].textContent.trim();
        console.log('🎮 Nav focus:', newIndex, buttons[newIndex].textContent.trim());
      }
    };
    
    const handleSelect = () => {
      if (!isFocused) return;
      const buttons = getNavButtons();
      if (focusedIndex >= 0 && buttons[focusedIndex]) {
        lastFocusedText.current = buttons[focusedIndex].textContent.trim();
        console.log('🎮 Select:', buttons[focusedIndex].textContent.trim());
        buttons[focusedIndex].click();
      }
    };
    
    const handleBack = () => {
      // Go back one level - collapse current
      const buttons = getNavButtons();
      if (focusedIndex >= 0 && buttons[focusedIndex]) {
        lastFocusedText.current = buttons[focusedIndex].textContent.trim();
        buttons[focusedIndex].click();
      }
    };
    
    window.addEventListener('gamepadNavigate', handleNavigate);
    window.addEventListener('gamepadSelect', handleSelect);
    window.addEventListener('gamepadBack', handleBack);
    
    return () => {
      window.removeEventListener('gamepadNavigate', handleNavigate);
      window.removeEventListener('gamepadSelect', handleSelect);
      window.removeEventListener('gamepadBack', handleBack);
    };
  }, [focusedIndex, isFocused]);
  
  // Restore focus after accordion structure changes
  useEffect(() => {
    // Small delay to let DOM update
    const timer = setTimeout(() => {
      const buttons = getNavButtons();
      
      // Clear any existing focus
      buttons.forEach(btn => btn.classList.remove('gamepad-focused'));
      
      if (lastFocusedText.current) {
        // Try to find the button with matching text
        const matchIndex = buttons.findIndex(btn => 
          btn.textContent.trim() === lastFocusedText.current
        );
        
        if (matchIndex >= 0) {
          buttons[matchIndex].classList.add('gamepad-focused');
          buttons[matchIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          setFocusedIndex(matchIndex);
          console.log('🎮 Restored focus to:', matchIndex, lastFocusedText.current);
        } else {
          // If exact match not found, keep previous index if valid
          if (focusedIndex >= 0 && focusedIndex < buttons.length) {
            buttons[focusedIndex].classList.add('gamepad-focused');
          }
        }
      }
    }, 50);
    
    return () => clearTimeout(timer);
  }, [expanded]);

  const [hierarchy, setHierarchy] = useState({
    languages: [],
    genres: {},      // genres by language
    subgenres: {},   // subgenres by genre
    artists: {},     // artists by subgenre
    albums: {}       // albums by artist
  });

  const [loading, setLoading] = useState(false);

  // Load hierarchy from Firestore ONCE on mount, or use cached if available
  useEffect(() => {
    // Only load if user is authenticated
    if (!user) {
      setLoading(false);
      return; // Don't navigate here - let the route protection handle it
    }

    // Check if we have a cached hierarchy that's still valid
    if (cachedHierarchy && cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_DURATION) {
      console.log('✅ Using cached hierarchy (no reload needed)');
      setHierarchy(cachedHierarchy);
      setLoading(false);
      if (onHierarchyLoad) {
        onHierarchyLoad(cachedHierarchy);
      }
      return; // Don't reload if we have valid cache
    }

    let isMounted = true; // Track if component is still mounted

    const loadHierarchy = async (retryCount = 0) => {
      // Wait for auth.currentUser to be ready
      let currentUser = auth.currentUser;
      if (!currentUser) {
        // Wait a bit for auth to initialize
        await new Promise(resolve => setTimeout(resolve, 500));
        currentUser = auth.currentUser;
      }
      
      if (!currentUser) {
        console.log('⏳ Waiting for auth.currentUser in LeftAccordion...');
        if (retryCount < 3) {
          // Retry after a delay
          setTimeout(() => loadHierarchy(retryCount + 1), 1000);
          return;
        } else {
          console.error('❌ Auth not ready after retries in LeftAccordion');
          setLoading(false);
          return;
        }
      }
      
      setLoading(true);
      const startTime = Date.now();
      console.log('⏳ Starting to fetch hierarchy from Firestore...');
      
      try {
        // Ensure auth token is ready
        const token = await currentUser.getIdToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        
        const q = query(collection(db, 'albums-flat'));
        const fetchStart = Date.now();
        const querySnapshot = await getDocs(q);
        const fetchTime = ((Date.now() - fetchStart) / 1000).toFixed(2);
        console.log(`📡 Firestore fetch completed in ${fetchTime}s (${querySnapshot.size} documents)`);
        
        // Check if component is still mounted before processing
        if (!isMounted) {
          console.log('⚠️ Component unmounted, aborting hierarchy load');
          return;
        }
        
        console.log('⚙️ Processing documents...');
        
        const languages = new Set();
        const genresByLang = {};
        const subgenresByGenre = {};
        const artistsBySubgenre = {};
        const albumsByArtist = {};

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const { language, genre, subgenre, artist, album } = data;

          // Skip if missing required fields
          if (!language || !genre) return;
          
          // DON'T filter by type here - load ALL data once
          // The filtering will happen in the rendering logic based on selectedPath.type
          
          // ALWAYS SHOW: Show all items even if files are missing in storage
          // if (storageStatus === 'unavailable') return;

          languages.add(language);

          const langKey = language;
          if (!genresByLang[langKey]) genresByLang[langKey] = new Set();
          genresByLang[langKey].add(genre);

          const genreKey = `${language}|${genre}`;
          if (subgenre) {
            if (!subgenresByGenre[genreKey]) subgenresByGenre[genreKey] = new Set();
            subgenresByGenre[genreKey].add(subgenre);
          }

          const subgenreKey = `${language}|${genre}|${subgenre || 'None'}`;
          if (artist) {
            if (!artistsBySubgenre[subgenreKey]) artistsBySubgenre[subgenreKey] = new Set();
            artistsBySubgenre[subgenreKey].add(artist);
          }

          const artistKey = `${language}|${genre}|${subgenre || 'None'}|${artist}`;
          if (album) {
            if (!albumsByArtist[artistKey]) albumsByArtist[artistKey] = [];
            albumsByArtist[artistKey].push({ id: doc.id, ...data });
          }
        });

        // Check again before updating state
        if (!isMounted) {
          console.log('⚠️ Component unmounted, aborting state update');
          return;
        }

        const genresObj = {};
        Object.keys(genresByLang).forEach(key => {
          genresObj[key] = Array.from(genresByLang[key]).sort();
        });

        const subgenresObj = {};
        Object.keys(subgenresByGenre).forEach(key => {
          subgenresObj[key] = Array.from(subgenresByGenre[key]).sort();
        });

        const artistsObj = {};
        Object.keys(artistsBySubgenre).forEach(key => {
          artistsObj[key] = Array.from(artistsBySubgenre[key]).sort();
        });

        const hierarchyData = {
          languages: Array.from(languages).sort(),
          genres: genresObj,
          subgenres: subgenresObj,
          artists: artistsObj,
          albums: albumsByArtist
        };

        const processingTime = ((Date.now() - startTime) / 1000).toFixed(2);
        console.log(`✅ Data processing completed in ${processingTime}s`);
        console.log(`   Languages: ${languages.size}, Artist groups: ${Object.keys(albumsByArtist).length}`);
        
        const stateUpdateStart = Date.now();
        setHierarchy(hierarchyData);
        
        // Cache the hierarchy for future use (survives component unmounts)
        cachedHierarchy = hierarchyData;
        cacheTimestamp = Date.now();
        console.log('💾 Cached hierarchy for future use');
        
        // Pass to parent if callback provided
        if (onHierarchyLoad) {
          onHierarchyLoad(hierarchyData);
        }
        
        const totalTime = ((Date.now() - startTime) / 1000).toFixed(2);
        const stateTime = ((Date.now() - stateUpdateStart) / 1000).toFixed(2);
        console.log(`✅ TOTAL LOAD TIME: ${totalTime}s`);
        console.log(`   - Firestore fetch: shown above`);
        console.log(`   - Data processing: ${processingTime}s`);
        console.log(`   - State update: ${stateTime}s`);
      } catch (error) {
        console.error('❌ Error loading hierarchy:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          name: error.name,
          user: user?.email,
          authUser: currentUser?.email
        });
        
        // Handle permission errors with retry
        if (error.code === 'permission-denied') {
          console.error('🔒 Permission denied. User might not be fully authenticated.');
          if (retryCount < 2 && isMounted) {
            // Retry after a delay
            console.log(`🔄 Retrying in 1 second... (attempt ${retryCount + 1})`);
            setTimeout(() => loadHierarchy(retryCount + 1), 1000);
            return;
          } else if (isMounted && user) {
            alert('Permission denied. Please refresh the page and make sure you are logged in.');
          }
        } else if (isMounted && user && error.message.includes('permission')) {
          alert('Session expired. Please log in again.');
          navigate('/login');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadHierarchy();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
      console.log('🧹 LeftAccordion cleanup: preventing stale state updates');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only reload when user changes, NOT when type changes!

  // Clear cache function - kept for potential future use
  // const clearCache = async () => {
  //   try {
  //     // Clear localStorage
  //     localStorage.clear();
  //     
  //     // Clear sessionStorage
  //     sessionStorage.clear();
  //     
  //     // Clear IndexedDB
  //     if (window.indexedDB) {
  //       const databases = await window.indexedDB.databases();
  //       databases.forEach(db => {
  //         window.indexedDB.deleteDatabase(db.name);
  //       });
  //     }
  //     
  //     // Clear service worker caches
  //     if ('caches' in window) {
  //       const cacheNames = await caches.keys();
  //       await Promise.all(cacheNames.map(name => caches.delete(name)));
  //     }
  //     
  //     // Unregister service workers
  //     if ('serviceWorker' in navigator) {
  //       const registrations = await navigator.serviceWorker.getRegistrations();
  //       await Promise.all(registrations.map(reg => reg.unregister()));
  //     }
  //     
  //     alert('Cache cleared! Redirecting to login...');
  //     // Redirect to login instead of reloading
  //     window.location.href = '/login';
  //   } catch (error) {
  //     console.error('Error clearing cache:', error);
  //     alert('Failed to clear cache. Please clear your browser cache manually.');
  //   }
  // };

  const handleTypeClick = (type) => {
    const typeKey = type.toLowerCase();
    const isCurrentlyExpanded = expanded[typeKey];
    
    // Close both types and open only this one
    setExpanded(prev => ({
      ...prev,
      albums: type === 'Albums' ? !isCurrentlyExpanded : false,
      singles: type === 'Singles' ? !isCurrentlyExpanded : false,
      languages: {}, // Close all languages
      genres: {}, // Close all genres
      subgenres: {}, // Close all subgenres
    }));
    onPathChange({ type, language: null, genre: null, subgenre: null, artist: null });
  };

  const handleLanguageClick = (language) => {
    const key = `lang_${selectedPath.type}_${language}`;
    const isCurrentlyExpanded = expanded.languages[key];
    
    // Close all languages and open only this one
    // Only expand the current level (genres), hide all deeper levels
    setExpanded(prev => ({
      ...prev,
      languages: { [key]: !isCurrentlyExpanded },
      genres: {}, // Close all genres - will be opened when genre is clicked
      subgenres: {}, // Close all subgenres
      artists: {}, // Close all artists
    }));
    onPathChange({ ...selectedPath, language, genre: null, subgenre: null, artist: null });
  };

  const handleGenreClick = (genre) => {
    const key = `genre_${selectedPath.type}_${selectedPath.language}_${genre}`;
    const isCurrentlyExpanded = expanded.genres[key];
    
    // Close all genres and open only this one
    setExpanded(prev => ({
      ...prev,
      genres: { [key]: !isCurrentlyExpanded },
      subgenres: {}, // Close all subgenres
    }));
    onPathChange({ ...selectedPath, genre, subgenre: null, artist: null });
  };

  const handleSubgenreClick = (subgenre) => {
    const key = `subgenre_${selectedPath.type}_${selectedPath.language}_${selectedPath.genre}_${subgenre}`;
    const isCurrentlyExpanded = expanded.subgenres[key];
    
    // Close all subgenres and open only this one
    // Only expand the current level (artists), hide all deeper levels
    setExpanded(prev => ({
      ...prev,
      subgenres: { [key]: !isCurrentlyExpanded },
      artists: {}, // Close all artists - will be shown when subgenre is selected
    }));
    onPathChange({ ...selectedPath, subgenre, artist: null });
  };

  const handleArtistClick = (artist) => {
    // Only use toggle mode when in master playlist mode
    // Otherwise use path-based navigation (normal browsing)
    if (isMasterPlaylistMode && onArtistToggle) {
      onArtistToggle(artist);
    } else {
      // Use path-based navigation for normal browsing
      onPathChange({ ...selectedPath, artist });
    }
  };

  if (loading) {
    return (
      <div className="accordion-loading">
        <p>Loading library...</p>
      </div>
    );
  }

  // Get unique, sorted artist names for dropdown (A-Z) - only from selectedFromMasterList
  const getSelectedArtistsForDropdown = () => {
    if (selectedFromMasterList.length === 0) return [];
    
    // Normalize artist names for grouping (remove numbers/suffixes)
    const normalizeArtistName = (name) => {
      if (!name) return '';
      return name.replace(/\s+\d+$/, '').trim();
    };
    
    // Group artists by normalized name
    const artistMap = new Map();
    selectedFromMasterList.forEach(artist => {
      const normalized = normalizeArtistName(artist);
      if (!artistMap.has(normalized)) {
        artistMap.set(normalized, []);
      }
      artistMap.get(normalized).push(artist);
    });
    
    // Get unique base names (normalized) and sort A-Z
    const uniqueBaseNames = Array.from(artistMap.keys()).sort();
    
    // Return array with base name and all variations
    return uniqueBaseNames.map(baseName => ({
      baseName,
      variations: artistMap.get(baseName).sort()
    }));
  };

  const dropdownArtists = getSelectedArtistsForDropdown();
  
  // Get all unique artists for the Master Artist list display
  const getAllUniqueArtists = () => {
    // Normalize artist names for grouping (remove numbers/suffixes)
    const normalizeArtistName = (name) => {
      if (!name) return '';
      return name.replace(/\s+\d+$/, '').trim();
    };
    
    // Group artists by normalized name
    const artistMap = new Map();
    selectedArtists.forEach(artist => {
      const normalized = normalizeArtistName(artist);
      if (!artistMap.has(normalized)) {
        artistMap.set(normalized, []);
      }
      artistMap.get(normalized).push(artist);
    });
    
    // Get unique base names (normalized) and sort A-Z
    const uniqueBaseNames = Array.from(artistMap.keys()).sort();
    
    // Return array with base name and all variations
    return uniqueBaseNames.map(baseName => ({
      baseName,
      variations: artistMap.get(baseName).sort()
    }));
  };

  const allUniqueArtists = getAllUniqueArtists();

  return (
    <div className={`left-accordion ${isCollapsed ? 'collapsed' : ''}`} ref={accordionRef}>
      {/* Master Artist list Mode Controls - Hidden by default */}
      {false && (
        <div className="master-playlist-controls" style={{ padding: '0.75rem 1rem', borderBottom: 'none', background: 'linear-gradient(180deg, #1a1a1a 0%, #121212 100%)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: selectedArtists.length > 0 ? '0.5rem' : '0' }}>
            <input
              type="checkbox"
              checked={isMasterPlaylistMode}
              onChange={(e) => onMasterPlaylistModeChange?.(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ color: '#fff', fontWeight: '600', fontSize: '0.95rem' }}>Master Artist list</span>
          </label>
          
          {/* Dropdown to view selected artists from Master Artist list */}
          {isMasterPlaylistMode && (
            <div style={{ marginTop: '0.5rem' }}>
              <select
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: '#2a2a2a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
                size={Math.min(Math.max(dropdownArtists.length, 3), 8)}
                multiple
                title={dropdownArtists.length > 0 ? `${selectedFromMasterList.length} artist(s) selected` : 'No artists selected from Master Artist list'}
              >
                {dropdownArtists.length === 0 ? (
                  <option disabled style={{ padding: '0.25rem', color: '#888' }}>
                    No artists selected - Click artists below to add them
                  </option>
                ) : (
                  dropdownArtists.map(({ baseName, variations }) => (
                    <option key={baseName} value={baseName} style={{ padding: '0.25rem' }}>
                      {baseName} {variations.length > 1 ? `(${variations.length} variations)` : ''}
                    </option>
                  ))
                )}
              </select>
              <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#aaa', textAlign: 'right' }}>
                {dropdownArtists.length > 0 ? `${selectedFromMasterList.length} selected` : '0 selected'}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Clear Cache Button - Removed for cleaner UI */}
      {/* Uncomment below to restore cache button if needed:
      <div style={{ padding: '1rem', borderBottom: 'none' }}>
        <button onClick={clearCache} style={{ width: '100%', padding: '0.75rem', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' }}>
          🗑️ Clear Cache & Reload
        </button>
      </div>
      */}

      <div className={`accordion-scrollable-content ${isMasterPlaylistMode ? 'master-mode-active' : ''}`}>
        {/* New Row-Based Navigation Layout */}
        <div className="nav-rows-container">
          {/* Row 1: Music Types */}
          <div className="nav-row nav-row-types">
            <div className="nav-row-content">
              <div className="nav-row-buttons">
              <button
                className={`nav-button ${selectedPath.type === 'Albums' ? 'active' : ''}`}
                onClick={() => handleTypeClick('Albums')}
              >
                Albums
              </button>
              <button
                className={`nav-button ${selectedPath.type === 'Singles' ? 'active' : ''}`}
                onClick={() => handleTypeClick('Singles')}
              >
                Singles
              </button>
              </div>
            </div>
          </div>

          {/* Row 2: Languages - Accordion */}
          {selectedPath.type && hierarchy && (
            <div className={`nav-row nav-row-languages ${selectedPath.type ? 'expanded' : 'collapsed'}`}>
              <div className="nav-row-content">
                <div className="nav-row-buttons">
                  {hierarchy.languages && hierarchy.languages
                    .filter(lang => {
                      // Only show languages that have content for the selected type
                      const genresForLang = hierarchy.genres[lang] || [];
                      return genresForLang.some(genre => {
                        const artistKey = Object.keys(hierarchy.albums || {}).find(key => 
                          key.startsWith(`${lang}|${genre}|`) && 
                          hierarchy.albums[key].some(album => album.type === selectedPath.type)
                        );
                        return !!artistKey;
                      });
                    })
                    .map(language => (
                      <button
                        key={language}
                        className={`nav-button ${selectedPath.language === language ? 'active' : ''}`}
                        onClick={() => handleLanguageClick(language)}
                      >
                        {language}
                      </button>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Row 3: Genre - Accordion - Only show when language is selected but genre is not */}
          {selectedPath.type && selectedPath.language && !selectedPath.genre && hierarchy && (
            <div className={`nav-row nav-row-genres ${selectedPath.language ? 'expanded' : 'collapsed'}`}>
              <div className="nav-row-content">
                <div className="nav-row-buttons">
                  {(hierarchy.genres[selectedPath.language] || []).map(genre => (
                    <button
                      key={genre}
                      className={`nav-button ${selectedPath.genre === genre ? 'active' : ''}`}
                      onClick={() => handleGenreClick(genre)}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Row 4: Subgenre - Accordion - Only show when genre is selected but subgenre is not */}
          {selectedPath.type && selectedPath.language && selectedPath.genre && !selectedPath.subgenre && hierarchy && (
            <div className={`nav-row nav-row-subgenres ${selectedPath.genre ? 'expanded' : 'collapsed'}`}>
              <div className="nav-row-content">
                <div className="nav-row-buttons">
                  {(hierarchy.subgenres[`${selectedPath.language}|${selectedPath.genre}`] || []).length > 0 ? (
                    (hierarchy.subgenres[`${selectedPath.language}|${selectedPath.genre}`] || []).map(subgenre => (
                      <button
                        key={subgenre}
                        className={`nav-button ${selectedPath.subgenre === subgenre ? 'active' : ''}`}
                        onClick={() => handleSubgenreClick(subgenre)}
                      >
                        {subgenre}
                      </button>
                    ))
                  ) : (
                    <div className="nav-row-empty">No subgenres available</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Row 5: Artists - Accordion - Only show when subgenre is selected */}
          {selectedPath.type && selectedPath.subgenre && hierarchy && (() => {
            // Collect all artists directly from albums (more reliable than hierarchy.artists)
            const artistsSet = new Set();
            Object.keys(hierarchy.albums || {}).forEach(artistKey => {
              // artistKey format: "language|genre|subgenre|artist"
              const [lang, genre, subgenre, artist] = artistKey.split('|');
              
              // Check if this artist's albums match the current selection
              const matches = 
                (!selectedPath.language || lang === selectedPath.language) &&
                (!selectedPath.genre || genre === selectedPath.genre) &&
                (!selectedPath.subgenre || subgenre === selectedPath.subgenre) &&
                (!selectedPath.artist || artist === selectedPath.artist);
              
              if (matches) {
                // Check if any album in this artist key matches the type
                const hasMatchingType = hierarchy.albums[artistKey].some(album => 
                  !selectedPath.type || album.type === selectedPath.type
                );
                
                if (hasMatchingType) {
                  // Collect artists from albums (album.artist is more reliable)
                  hierarchy.albums[artistKey].forEach(album => {
                    if (!selectedPath.type || album.type === selectedPath.type) {
                      if (album.artist) {
                        artistsSet.add(album.artist);
                      }
                    }
                  });
                }
              }
            });
            const artistsList = Array.from(artistsSet).sort();
            
            // Hide artist buttons row when in Master Artist list mode
            if (isMasterPlaylistMode) {
              return null;
            }
            
            return (
              <div className={`nav-row nav-row-artists ${selectedPath.type ? 'expanded' : 'collapsed'}`}>
                <div className="nav-row-content" ref={artistsScrollRef}>
                  <div className="nav-row-buttons">
                    {artistsList.length > 0 ? (
                      artistsList.map(artist => (
                        <button
                          key={artist}
                          className={`nav-button ${selectedArtists.includes(artist) ? 'active' : ''}`}
                          onClick={() => handleArtistClick(artist)}
                          title={selectedArtists.includes(artist) ? `Selected: ${artist}` : `Click to select ${artist}`}
                        >
                          {artist}
                          {selectedArtists.includes(artist) && ' ✓'}
                        </button>
                      ))
                    ) : (
                      <div className="nav-row-empty">No artists available</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

      {/* Albums - Hidden */}
      <div className="accordion-item">
        <AccordionImageButton
          imagePath="Albums/album.jpg"
          label="Albums"
          isExpanded={expanded.albums}
          onClick={() => handleTypeClick('Albums')}
        />
        
        {expanded.albums && (
          <div className="accordion-content">
            {hierarchy.languages.filter(lang => {
              // Only show languages that have Albums
              const genresForLang = hierarchy.genres[lang] || [];
              return genresForLang.some(genre => {
                const artistKey = Object.keys(hierarchy.albums).find(key => 
                  key.startsWith(`${lang}|${genre}|`) && 
                  hierarchy.albums[key].some(album => album.type === 'Albums')
                );
                return !!artistKey;
              });
            }).map(language => (
              <div key={language} className="accordion-nested">
                <AccordionImageButton
                  imagePath={`Albums/${language}/album.jpg`}
                  label={language}
                  isExpanded={expanded.languages[`lang_Albums_${language}`]}
                  onClick={() => handleLanguageClick(language)}
                  className="nested"
                />
                
                {expanded.languages[`lang_Albums_${language}`] && (
                  <div className="accordion-content">
                    {(hierarchy.genres[language] || []).map(genre => (
                      <div key={genre} className="accordion-nested">
                        <AccordionImageButton
                          imagePath={`Albums/${language}/${genre}/album.jpg`}
                          label={genre}
                          isExpanded={expanded.genres[`genre_Albums_${language}_${genre}`]}
                          onClick={() => handleGenreClick(genre)}
                          className="nested level-2"
                        />
                        
                        {expanded.genres[`genre_Albums_${language}_${genre}`] && selectedPath.genre === genre && (
                          <div className="accordion-content">
                            {(hierarchy.subgenres[`${language}|${genre}`] || []).map(subgenre => (
                              <div key={subgenre} className="accordion-nested">
                                <AccordionImageButton
                                  imagePath={`Albums/${language}/${genre}/${subgenre}/album.jpg`}
                                  label={subgenre}
                                  isExpanded={expanded.subgenres[`subgenre_Albums_${language}_${genre}_${subgenre}`]}
                                  onClick={() => handleSubgenreClick(subgenre)}
                                  className="nested level-3"
                                />
                                
                                {expanded.subgenres[`subgenre_Albums_${language}_${genre}_${subgenre}`] && selectedPath.subgenre === subgenre && (
                                  <div className="accordion-content">
                                    {(hierarchy.artists[`${language}|${genre}|${subgenre}`] || []).map(artist => (
                                      <AccordionImageButton
                                        key={artist}
                                        imagePath={`Albums/${language}/${genre}/${subgenre}/${artist}/album.jpg`}
                                        label={selectedArtists.includes(artist) ? `${artist} ✓` : artist}
                                        isExpanded={false}
                                        onClick={() => handleArtistClick(artist)}
                                        className={`nested level-4 ${selectedArtists.includes(artist) ? 'active' : ''}`}
                                        showExpandIcon={false}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Singles */}
      <div className="accordion-item">
        <AccordionImageButton
          imagePath="Singles/album.jpg"
          label="Singles"
          isExpanded={expanded.singles}
          onClick={() => handleTypeClick('Singles')}
        />
        
        {expanded.singles && (
          <div className="accordion-content">
            {hierarchy.languages.filter(lang => {
              // Only show languages that have Singles
              const genresForLang = hierarchy.genres[lang] || [];
              return genresForLang.some(genre => {
                const artistKey = Object.keys(hierarchy.albums).find(key => 
                  key.startsWith(`${lang}|${genre}|`) && 
                  hierarchy.albums[key].some(album => album.type === 'Singles')
                );
                return !!artistKey;
              });
            }).map(language => {
              // For Singles, check for two possible structures:
              // 1. Singles/Language/Various/Variety/Mix/[Playlists] (English) - Direct playlists
              // 2. Singles/Language/Various/Variety/Mix/[Artists]/[Playlists] (Spanish) - Artist folders with playlists inside
              
              // Get playlists/items under Singles/Language/Various/Variety/Mix
              const mixKey = `${language}|Various|Variety`;
              const mixItems = hierarchy.artists[mixKey] || [];
              
              // Determine if this language needs expansion (has nested structure like Spanish)
              // If there are artist folders under Various/Variety, it needs expansion
              const hasNestedStructure = mixItems.length > 0;
              
              // Count total albums for this language
              const totalAlbumsCount = Object.keys(hierarchy.albums)
                .filter(key => key.startsWith(`${language}|Various|`) && 
                        hierarchy.albums[key].some(album => album.type === 'Singles'))
                .reduce((sum, key) => sum + hierarchy.albums[key].filter(a => a.type === 'Singles').length, 0);
              
              if (!hasNestedStructure) {
                // English-style: Direct filter button (no expansion needed)
                return (
                  <div key={language} className="accordion-nested">
                    <AccordionImageButton
                      imagePath={`Singles/${language}/album.jpg`}
                      label={`${language} (${totalAlbumsCount})`}
                      isExpanded={false}
                      onClick={() => {
                        // Direct filter: Don't expand, just show all Singles for this language
                        onPathChange({
                          type: 'Singles',
                          language: language,
                          genre: null,
                          subgenre: null,
                          artist: null
                        });
                      }}
                      className={`nested ${selectedPath.language === language && selectedPath.type === 'Singles' ? 'active' : ''}`}
                      showExpandIcon={false}
                    />
                  </div>
                );
              } else {
                // Spanish-style: Expandable with artist folders
                return (
                  <div key={language} className="accordion-nested">
                    <AccordionImageButton
                      imagePath={`Singles/${language}/album.jpg`}
                      label={`${language} (${totalAlbumsCount})`}
                      isExpanded={expanded.languages[`lang_Singles_${language}`]}
                      onClick={() => handleLanguageClick(language)}
                      className="nested"
                    />
                    
                    {expanded.languages[`lang_Singles_${language}`] && (
                      <div className="accordion-content">
                        <div className="singles-playlists-container" style={{
                          maxHeight: '400px',
                          overflowY: 'auto',
                          overflowX: 'hidden'
                        }}>
                          {mixItems.map(artistFolder => {
                            // Get the albums/playlists for this specific artist folder
                            const artistPlaylistsKey = `${language}|Various|Variety|${artistFolder}`;
                            const artistPlaylists = hierarchy.albums[artistPlaylistsKey] || [];
                            
                            return (
                              <div key={artistFolder} className="accordion-nested">
                                <AccordionImageButton
                                  imagePath={`Singles/${language}/Various/Variety/Mix/${artistFolder}/album.jpg`}
                                  label={`${artistFolder} (${artistPlaylists.length})`}
                                  isExpanded={expanded.subgenres[`subgenre_Singles_${language}_Various_${artistFolder}`]}
                                  onClick={() => {
                                    const key = `subgenre_Singles_${language}_Various_${artistFolder}`;
                                    const isCurrentlyExpanded = expanded.subgenres[key];
                                    setExpanded(prev => ({
                                      ...prev,
                                      subgenres: { [key]: !isCurrentlyExpanded }
                                    }));
                                  }}
                                  className="nested level-2"
                                />
                                
                                {expanded.subgenres[`subgenre_Singles_${language}_Various_${artistFolder}`] && (
                                  <div className="accordion-content singles-playlists-container" style={{
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    overflowX: 'hidden'
                                  }}>
                                    {artistPlaylists.map(albumObj => (
                                      <AccordionImageButton
                                        key={albumObj.id}
                                        imagePath={`Singles/${language}/Various/Variety/Mix/${artistFolder}/${albumObj.album}/album.jpg`}
                                        label={albumObj.album || albumObj.artist || 'Unknown'}
                                        isExpanded={false}
                                        onClick={() => {
                                          // Set path for filtering
                                          onPathChange({
                                            type: 'Singles',
                                            language: language,
                                            genre: 'Various',
                                            subgenre: 'Variety',
                                            artist: artistFolder,
                                            album: albumObj.album
                                          });
                                          // Select and play the album
                                          if (onAlbumSelect) {
                                            onAlbumSelect(albumObj);
                                          }
                                          // Close nav panel on mobile, collapse on desktop
                                          if (window.innerWidth <= 768 && onClose) {
                                            onClose();
                                          } else if (onDoneSearching) {
                                            onDoneSearching();
                                          }
                                        }}
                                        className={`nested level-3 ${selectedPath.album === albumObj.album && selectedPath.artist === artistFolder ? 'active' : ''}`}
                                        showExpandIcon={false}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
            })}
          </div>
        )}
      </div>
      </div>

      {/* Mobile Navigation Links Toggle Button - Moved to floating action buttons */}

      {/* Mobile Navigation Links */}
      {showNavLinks && (
        <div className="accordion-nav-links">
          <Link to="/" className="accordion-nav-link" onClick={onClose}>
            🏠 Home
          </Link>
          <Link to="/storage" className="accordion-nav-link" onClick={onClose}>
            📤 Upload Music
          </Link>
          <Link to="/services" className="accordion-nav-link" onClick={onClose}>
            🎵 Music Services
          </Link>
          <Link to="/roadtrip" className="accordion-nav-link" onClick={onClose}>
            🎉 Listening Party
          </Link>
          <button 
            className="accordion-nav-link logout-btn"
            onClick={async () => {
              try {
                // Clear hierarchy cache to free memory
                cachedHierarchy = null;
                cacheTimestamp = null;
                console.log('🧹 Cleared hierarchy cache before logout');
                
                await signOut(auth);
                navigate('/login');
              } catch (error) {
                console.error('Error signing out:', error);
              }
            }}
          >
            🚪 Logout
          </button>
        </div>
      )}
    </div>
  );
}

export default LeftAccordion;


